import i18n from '@/locales/i18n';
import { v4 as uuidv4 } from 'uuid';
import { merge } from 'lodash-es';
import { pythonDefaultCode } from '../enums';
import { getQueryParams } from '../tools';
export const blockType = {
  block: 'block',
  start: 'start'
};

/** Fixed child node ids for workflow subgraph start/end (pass as childId to generateChildNode). */
export const WORKFLOW_SUBGRAPH_START_NODE_ID = 'start00000000000000000000';
export const WORKFLOW_SUBGRAPH_END_NODE_ID = 'end00000000000000000000';
export const nodeType = {
  llMReply: 'llMReply',
  textReply: 'textReply',
  button: 'button',
  cardReply: 'cardReply',
  captureUserReply: 'captureUserReply',
  captureVoiceReply: 'captureVoiceReply',
  voiceReply: 'voiceReply',
  captureSlotEntity: 'captureSlotEntity',
  llmVariableAssignment: 'llmVariableAssignment',
  variableAssignment: 'variableAssignment',
  textToImage: 'textToImage',
  condition: 'condition',
  semanticJudgment: 'semanticJudgment',
  actionInstruction: 'actionInstruction',
  actionApi: 'actionApi',
  asyncTask: 'asyncTask',
  asyncTaskTrigger: 'asyncTaskTrigger',
  code: 'code',
  jump: 'jump',
  jumpAgent: 'jumpAgent',
  knowledgeAssignment: 'knowledgeAssignment',
  agenticReply: 'agenticReply',
  agenticAssignment: 'agenticAssignment',
  longMemory: 'longMemory',
  fileUpload: 'fileUpload',
  fileSearch: 'fileSearch',
  fileParse: 'fileParse',
  database: 'database',
  manual: 'manual',
  plugin: 'plugin',
  wf_input: 'wf_input',
  wf_output: 'wf_output'
};
export const nodeDragSource = {
  menu: 'NENU',
  flow: 'FLOW',
  node: 'NODE'
};
export const generatorBlockNode = ({ label, position, id, childId }) => {
  return {
    id,
    type: 'block',
    data: {
      label,
      include_node_ids: [childId]
    },
    position
  };
};
export const generateChildNode = ({ type, blockId, childId, config }) => {
  const isAiWrite = getQueryParams('chatflowType') === 'ai_write';
  const templates = {
    [nodeType.textReply]: () => ({
      id: childId,
      blockId,
      hidden: true,
      position: {
        x: 0,
        y: 0
      },
      type,
      config: {
        async_run: false,
        plain_text: [
          {
            text: '',
            id: uuidv4()
          }
        ],
        rich_text: []
      },
      data: {
        sourceHandle: uuidv4()
      }
    }),
    [nodeType.cardReply]: () => ({
      id: childId,
      blockId,
      hidden: true,
      position: {
        x: 0,
        y: 0
      },
      type,
      config: {
        card_id: '',
        card_name: '',
        card_version: '',
        card_version_id: '',
        bind_card_type: 2,
        card_session_type: 1,
        description: '',
        variable_mapping: '{}'
      },
      data: {
        sourceHandle: uuidv4()
      }
    }),
    [nodeType.llMReply]: () => ({
      id: childId,
      blockId,
      type,
      hidden: true,
      position: {
        x: 0,
        y: 0
      },
      config: merge(
        {
          desc: '',
          prompt_template: '',
          tool_list: [],
          tool_enabled: false,
          llm_config: {
            slang_enable: false,
            ltm_threshold_simi: 50,
            divergence: 2,
            prompt: '',
            llm_name: '',
            top_p: 1,
            enable_thinking: 0,
            deep_thinking_enable: false,
            deep_thinking_level: '',
            rag_question: '',
            rag_range: '',
            rag_enabled: '',
            knowledge_base_ids: [],
            ai_tag_list: [],
            knowledge_base_list: [],
            knowledge_search_flag: false,
            search_type: 1,
            rerank_enable: true,
            rerank_model: '',
            rerank_correlation_threshold: 0.65,
            rerank_max_reference_single_folder_num: 3,
            chat_history_flag: true,
            chat_history_count: 5,
            ltm_enabled: false,
            ltm_search_range: '0',
            ltm_robot_ids: [],
            ltm_question: '',
            ltm_recall_count: 5,
            verify_enable: false,
            verify_count: 5,
            verify_constraints: '',
            main_condition_id: uuidv4(),
            other_condition_id: uuidv4()
          }
        },
        config
      ),
      data: {
        sourceHandle: uuidv4()
      }
    }),
    [nodeType.captureUserReply]: () => ({
      id: childId,
      blockId,
      type,
      hidden: true,
      position: {
        x: 0,
        y: 0
      },
      config: {
        variable_assign: 'last_user_response'
      },
      data: {
        sourceHandle: uuidv4()
      }
    }),
    [nodeType.captureVoiceReply]: () => ({
      id: childId,
      blockId,
      type,
      hidden: true,
      position: {
        x: 0,
        y: 0
      },
      config: {
        variable_assign: 'last_user_response',
        enable_global_intention: false,
        voice_source: 0,
        guide_text: '',
        voice_config: {
          asr: {
            model_id: '',
            model_name: '',
            hot_words: ''
          },
          vad: {
            wait_time_ms: 500
          }
        }
      },
      data: {
        sourceHandle: uuidv4()
      }
    }),
    [nodeType.voiceReply]: () => ({
      id: childId,
      blockId,
      type,
      hidden: true,
      position: {
        x: 0,
        y: 0
      },
      config: {
        voice_source: 0,
        voice_config: {
          allow_interrupt: 1
        },
        plain_text: [
          {
            text: '',
            id: uuidv4()
          }
        ],
        rich_text: []
      },
      data: {
        sourceHandle: uuidv4()
      }
    }),
    [nodeType.captureSlotEntity]: () => ({
      id: childId,
      blockId,
      type,
      hidden: true,
      position: {
        x: 0,
        y: 0
      },
      config: merge(
        {
          collect_mode: 'function_call',
          entities: [
            {
              entity_name: '',
              verify_type: '0',
              fixed_times_num: 1,
              entity_verify_requirements: '',
              verify_failed_instruction: '',
              guide_text: {
                llm: '',
                fixed: ''
              },
              limit: 1,
              failed_text: {
                llm: '',
                fixed: ''
              },
              required: '1'
            }
          ],
          function_call: {
            guide_text: {
              llm: '',
              fixed: ''
            },
            required: '1',
            verify_type: '0',
            fixed_times_num: 1,
            entities: [
              {
                id: '',
                entity_name: '',
                required: true
              }
            ]
          },
          llm_config: {
            divergence: 2,
            llm_name: '',
            top_p: 1,
            max_tokens: 2048
          },
          regather_clear_entities: true,
          enable_global_intention: false,
          slot_result_verify: '1'
        },
        config
      ),
      data: {
        sourceHandle: uuidv4(),
        failHandle: uuidv4()
      }
    }),
    [nodeType.llmVariableAssignment]: () => ({
      id: childId,
      blockId,
      type,
      hidden: true,
      position: {
        x: 0,
        y: 0
      },
      config: merge(
        {
          title: '',
          desc: '',
          prompt_template: '',
          variable_assign: '',
          async_run: false,
          tool_list: [],
          tool_enabled: false,
          llm_config: {
            divergence: 2,
            prompt: '',
            llm_name: '',
            top_p: 1,
            enable_thinking: 0,
            deep_thinking_enable: false,
            deep_thinking_level: '',
            rag_question: '',
            rag_range: '',
            rag_enabled: '',
            knowledge_base_ids: [],
            ai_tag_list: [],
            knowledge_base_list: [],
            knowledge_search_flag: false,
            search_type: 1,
            rerank_enable: true,
            rerank_model: '',
            rerank_correlation_threshold: 0.65,
            rerank_max_reference_single_folder_num: 3,
            chat_history_flag: false,
            chat_history_count: 5,
            ltm_enabled: false,
            ltm_search_range: '0',
            ltm_threshold_simi: 50,
            ltm_robot_ids: [],
            ltm_question: '',
            ltm_recall_count: 5
          }
        },
        config
      ),
      data: {
        sourceHandle: uuidv4()
      }
    }),
    [nodeType.variableAssignment]: () => ({
      id: childId,
      blockId,
      type,
      hidden: true,
      position: {
        x: 0,
        y: 0
      },
      config: {
        title: '',
        variable_assign: [
          {
            prompt_template: '',
            variable_assign: ''
          }
        ]
      },
      data: {
        sourceHandle: uuidv4()
      }
    }),
    [nodeType.textToImage]: () => ({
      id: childId,
      blockId,
      type,
      hidden: true,
      position: {
        x: 0,
        y: 0
      },
      config: merge(
        {
          title: i18n.global.t(
            'libs.flow.const.58320b17b8e41eed39eaae660fbd8644'
          ),
          model: '',
          aspect_ratio: '1:1',
          image_size: '',
          n: 1,
          prompt_template: '',
          reference_images: [],
          output_mode: 'image',
          variable_assign: '',
          exception_handling: {
            enabled: false,
            execution_timeout_seconds: 60,
            first_token_timeout_enabled: false,
            first_token_timeout_seconds: 5,
            retry_times: 0,
            strategy: 'interrupt',
            default_value: {},
            exception_branch_handle_id: uuidv4()
          }
        },
        config
      ),
      data: {
        sourceHandle: uuidv4()
      }
    }),
    [nodeType.condition]: () => ({
      id: childId,
      blockId,
      type,
      hidden: true,
      position: {
        x: 0,
        y: 0
      },
      config: {
        if_else_conditions: [
          {
            condition_id: uuidv4(),
            condition_name: '',
            logical_operator: 'and',
            conditions: [
              // {
              //   id: uuidv4(),
              //   condition_variable: '',
              //   condition_type: 'variable', // "variable" or "entity",
              //   comparison_operator: '=',
              //   condition_value: ''
              // }
            ],
            condition_action: []
          },
          {
            condition_id: uuidv4(),
            condition_name: i18n.global.t(
              'libs.flow.const.2debe6c16b22cbeebb3053b020f164b1'
            ),
            logical_operator: 'other',
            conditions: [],
            condition_action: []
          }
        ]
      },
      data: {
        sourceHandle: uuidv4()
      }
    }),
    [nodeType.semanticJudgment]: () => ({
      id: childId,
      blockId,
      type,
      hidden: true,
      position: {
        x: 0,
        y: 0
      },
      config: {
        semantic_conditions: [],
        default_condition: {
          condition_id: uuidv4(),
          name: i18n.global.t(
            'libs.flow.const.2debe6c16b22cbeebb3053b020f164b1'
          ),
          desc: '',
          refer_questions: [],
          condition_config: {
            keyword_enable: false,
            keywords: [],
            keyword_type: 0,
            regular_enable: false,
            regular_str: '',
            sft_model_enable: false,
            sft_model_name: '',
            sft_model_reponse_structure: {
              label: '',
              value: ''
            },
            llm_enable: true,
            embedding_enable: false
          }
        },
        global_config: {
          embedding_model_name: '',
          embedding_confidence: 65,
          embedding_rerank_model_name: '',
          embedding_rerank_confidence: 50,
          embedding_llm_model_name: '',
          embedding_llm_prompt: '',
          embedding_llm_return_count: 50,
          embedding_max_reference_knowledge_num: 3,
          embedding_semantic_proportion: 1,
          sft_model_name: ''
        }
      },
      data: {
        sourceHandle: uuidv4()
      }
    }),
    [nodeType.actionApi]: () => ({
      id: childId,
      blockId,
      type,
      hidden: true,
      position: {
        x: 0,
        y: 0
      },
      data: {
        sourceHandle: uuidv4()
      },
      config: {
        title: '',
        method: 'get',
        url: '',
        headers: [],
        params: [],
        body: {
          type: 'none',
          data: [],
          rowContent: ''
        },
        capture_response: [],
        inputs: {},
        ...(IS_DOMESTIC
          ? {
              network: 1
            }
          : {})
      }
    }),
    [nodeType.code]: () => ({
      id: childId,
      blockId,
      type,
      hidden: true,
      position: {
        x: 0,
        y: 0
      },
      data: {
        sourceHandle: uuidv4()
      },
      config: {
        title: '',
        desc: '',
        code: pythonDefaultCode,
        ...(IS_DOMESTIC ? { network: 1 } : {}),
        code_language: '',
        outputs: [
          {
            name: 'result',
            type: 'number'
          }
        ],
        args: [
          {
            name: 'args1',
            default_value: '<br>',
            type: 'integer'
          },
          {
            name: 'args2',
            default_value: '<br>',
            type: 'integer'
          }
        ]
      }
    }),
    [nodeType.jumpAgent]: () => ({
      id: childId,
      blockId,
      type,
      hidden: true,
      position: {
        x: 0,
        y: 0
      },
      config: {
        agent_id: '',
        agent_name: ''
      }
    }),
    [nodeType.jump]: () => ({
      id: childId,
      blockId,
      type,
      hidden: true,
      position: {
        x: 0,
        y: 0
      },
      config: {
        jump_type: '',
        jump_robot_id: '',
        jump_robot_name: '',
        jump_carry_histories: true,
        jump_carry_history_number: 5,
        jump_flow_name: '',
        jump_flow_uuid: '',
        jump_carry_userinput: true
      }
    }),
    [nodeType.knowledgeAssignment]: () => ({
      id: childId,
      blockId,
      type,
      hidden: true,
      position: {
        x: 0,
        y: 0
      },
      config: {
        title: '',
        variable_assign: '',
        rag_config: {
          rag_question: '',
          rag_range: '',
          knowledge_base_ids: [],
          ai_tag_list: [],
          knowledge_base_list: [],
          knowledge_search_flag: true,
          search_type: 1,
          keyword_enable: false,
          keywords: '',
          rag_correlation_threshold: isAiWrite ? 30 : 65,
          rag_max_reference_knowledge_num: isAiWrite ? 10 : 3,
          rerank_enable: true,
          rerank_model: '',
          rerank_correlation_threshold: 0.65,
          rerank_max_reference_single_folder_num: 3
        }
      },
      data: {
        sourceHandle: uuidv4()
      }
    }),
    [nodeType.agenticReply]: () => ({
      id: childId,
      blockId,
      type,
      hidden: true,
      position: {
        x: 0,
        y: 0
      },
      config: {
        title: '',
        desc: '',
        query: '',
        agentic_config: {
          knowledge_base_list: [],
          knowledge_base_ids: [],
          chat_model_name: '',
          rerank_model_name: '',
          prompt: '',
          max_context_num: 5,
          is_reasoning: true,
          temperature: 0.3
        }
      },
      data: {
        sourceHandle: uuidv4()
      }
    }),
    [nodeType.agenticAssignment]: () => ({
      id: childId,
      blockId,
      type,
      hidden: true,
      position: {
        x: 0,
        y: 0
      },
      config: {
        title: '',
        desc: '',
        query: '',
        variable_assign: '',
        agentic_config: {
          knowledge_base_list: [],
          knowledge_base_ids: [],
          chat_model_name: '',
          rerank_model_name: '',
          prompt: '',
          max_context_num: 5,
          is_reasoning: false,
          temperature: 0.3
        }
      },
      data: {
        sourceHandle: uuidv4()
      }
    }),
    [nodeType.longMemory]: () => ({
      id: childId,
      blockId,
      type,
      hidden: true,
      position: {
        x: 0,
        y: 0
      },
      config: {
        title: '',
        variable_assign: '',
        operate_type: 1,
        variable_configs: [],
        read_variable_configs: [],
        write_type: 1,
        ltm_config: {
          ltm_search_range: '0',
          ltm_robot_ids: [],
          ltm_question: '',
          ltm_recall_count: 5,
          ltm_threshold_simi: 50
        }
      },
      data: {
        sourceHandle: uuidv4()
      }
    }),
    [nodeType.fileUpload]: () => ({
      id: childId,
      blockId,
      type,
      hidden: true,
      position: {
        x: 0,
        y: 0
      },
      config: {
        instruction: '',
        upload_file_categories: [
          {
            id: '',
            name: '',
            support_formats: [],
            variable_assign_enable: true,
            variable_assign_id: '',
            variable_assign: '',
            variable_assign_file: '',
            assign_type: 0,
            upload_max_num: 1,
            is_parse_enabled: true,
            customized_parse_methods: [
              {
                file_format: '',
                parse_model: 'text',
                parse_prompt: '',
                with_time: false,
                with_emo: false,
                with_num_spks: false,
                with_spk_id: false
              }
            ]
          }
        ]
      },
      data: {
        sourceHandle: uuidv4()
      }
    }),
    [nodeType.fileSearch]: () => ({
      id: childId,
      blockId,
      type,
      hidden: true,
      position: {
        x: 0,
        y: 0
      },
      config: {
        title: '',
        desc: '',
        search_config: {
          knowledge_search_range: [],
          recall_file_enable: false,
          recall_file_num: 3,
          knowledge_search_conditions: {
            conditions: [],
            logical_operator: 'and'
          }
        }
      },
      data: {
        sourceHandle: uuidv4()
      }
    }),
    [nodeType.fileParse]: () => ({
      id: childId,
      blockId,
      type,
      hidden: true,
      position: {
        x: 0,
        y: 0
      },
      config: {
        upload_file_categories: [
          {
            id: '',
            name: '',
            support_formats: '',
            variable_assign_enable: true,
            variable_assign_id: '',
            variable_assign: '',
            upload_max_num: 1,
            parse_file_variables: [],
            customized_parse_methods: [
              {
                file_format: '',
                parse_model: 'text',
                parse_prompt: '',
                with_time: false
              }
            ]
          }
        ]
      },
      data: {
        sourceHandle: uuidv4()
      }
    }),
    [nodeType.manual]: () => ({
      id: childId,
      blockId,
      type,
      hidden: true,
      position: {
        x: 0,
        y: 0
      },
      config: {
        title: '',
        desc: ''
      },
      data: {
        sourceHandle: uuidv4()
      }
    }),
    [nodeType.plugin]: () => ({
      id: childId,
      blockId,
      type,
      hidden: true,
      position: {
        x: 0,
        y: 0
      },
      config: merge(
        {
          title: '',
          plugin_id: '',
          function_call_id: '',
          inputs: [],
          outputs: [],
          tool_id: '',
          plugin_version: '2.0.0'
        },
        config
      ),
      data: {
        sourceHandle: uuidv4()
      }
    }),
    [nodeType.database]: () => ({
      id: childId,
      blockId,
      type,
      hidden: true,
      position: {
        x: 0,
        y: 0
      },
      config: {
        component_type: 1,
        component_desc: '',
        variable_assign: [],
        agent_database_id: '',
        component: {
          table_list: [],
          self_define_sql: '',
          set_field_list: [],
          query_field_list: [],
          condition_list: [],
          condition_relation: '',
          query_limit: ''
        }
      },
      data: {
        sourceHandle: uuidv4()
      }
    }),
    [nodeType.wf_input]: () => ({
      id: childId,
      blockId,
      type,
      hidden: true,
      position: {
        x: 0,
        y: 0
      },
      config: {},
      data: {
        sourceHandle: uuidv4(),
        label: 'Start'
      }
    }),
    [nodeType.asyncTask]: () => ({
      id: childId,
      blockId,
      type,
      hidden: true,
      position: {
        x: 0,
        y: 0
      },
      data: {
        sourceHandle: uuidv4(),
        triggerHandle: uuidv4(),
        fallbackHandle: uuidv4(),
        triggerBlockId: ''
      },
      config: {
        title: '',
        desc: '',
        async_type: 'http',
        timeout: 0,
        http_data: {
          method: 'get',
          url: '',
          headers: [],
          params: [],
          body: {
            type: 'none',
            data: [],
            rowContent: ''
          },
          capture_response: []
        },
        plugin_data: {},
        card_data: {}
      }
    }),
    [nodeType.asyncTaskTrigger]: () => ({
      id: childId,
      blockId,
      type,
      hidden: true,
      position: {
        x: 0,
        y: 0
      },
      data: {
        sourceHandle: uuidv4(),
        asyncTaskBlockId: '',
        label: 'Trigger'
      },
      config: {
        title: '',
        callback_url: ''
      }
    }),
    [nodeType.wf_output]: () => ({
      id: childId,
      blockId,
      type,
      hidden: true,
      position: {
        x: 0,
        y: 0
      },
      config: {
        title: '',
        desc: '',
        async_run: 0,
        enable_global_intention: true,
        configuration: {
          return_type: 'json',
          json_output: {
            variables: [
              {
                name: '',
                type: '',
                value: '',
                value_source: 'variable'
              }
            ]
          },
          text_output: {
            template: ''
          }
        }
      },
      data: {
        label: i18n.global.t('libs.flow.const.cf921eb5ef56010114c0635fc45f556d')
      }
    })
  };
  return templates[type]();
};
export const getTypeArr = () => {
  return Object.keys(nodeType).map(key => nodeType[key]);
};
export const toolbarConfig = {
  toolbarKeys: [
    'undo',
    'redo',
    '|',
    'bold',
    'italic',
    {
      key: 'group-justify',
      iconSvg:
        '<svg viewBox="0 0 1024 1024"><path d="M768 793.6v102.4H51.2v-102.4h716.8z m204.8-230.4v102.4H51.2v-102.4h921.6z m-204.8-230.4v102.4H51.2v-102.4h716.8zM972.8 102.4v102.4H51.2V102.4h921.6z"></path></svg>',
      title: i18n.global.t('libs.flow.const.f89c8e0fe5863fe90da40caf3df8d100'),
      menuKeys: [
        'justifyLeft',
        'justifyRight',
        'justifyCenter',
        'justifyJustify'
      ]
    },
    '|',
    // {
    //   key: 'group-image',
    //   title: 'image',
    //   iconSvg:
    //     '<svg viewBox="0 0 1024 1024"><path d="M959.877 128l0.123 0.123v767.775l-0.123 0.122H64.102l-0.122-0.122V128.123l0.122-0.123h895.775zM960 64H64C28.795 64 0 92.795 0 128v768c0 35.205 28.795 64 64 64h896c35.205 0 64-28.795 64-64V128c0-35.205-28.795-64-64-64zM832 288.01c0 53.023-42.988 96.01-96.01 96.01s-96.01-42.987-96.01-96.01S682.967 192 735.99 192 832 234.988 832 288.01zM896 832H128V704l224.01-384 256 320h64l224.01-192z"></path></svg>',
    //   menuKeys: []
    // },
    {
      key: 'group-more-style',
      title: i18n.global.t('libs.flow.const.e3765c5e47e65f21f4b7f0474a1335d8'),
      iconSvg:
        '<svg viewBox="0 0 1024 1024">' +
        '<path d="M204.8 505.6m-76.8 0a76.8 76.8 0 1 0 153.6 0 76.8 76.8 0 1 0-153.6 0Z"></path>' +
        '<path d="M505.6 505.6m-76.8 0a76.8 76.8 0 1 0 153.6 0 76.8 76.8 0 1 0-153.6 0Z"></path>' +
        '<path d="M806.4 505.6m-76.8 0a76.8 76.8 0 1 0 153.6 0 76.8 76.8 0 1 0-153.6 0Z"></path>' +
        '</svg>',
      menuKeys: [
        'bulletedList',
        'numberedList',
        'indent',
        'delIndent',
        'emotion'
        // 'insertLink'
      ]
    }
  ]
};
export const fineNameConfigOptions = [
  {
    label: i18n.global.t('libs.flow.const.57a4d26f94764fbdf55f6977b5920ce0'),
    value: 'contains'
  },
  {
    label: i18n.global.t('libs.flow.const.4449c13d7633323a81ebeb8ffa6cba36'),
    value: 'not contains'
  },
  {
    label: i18n.global.t('libs.flow.const.d86c28c50f44bb30dd2028e3b3cf772a'),
    value: '='
  },
  {
    label: i18n.global.t('libs.flow.const.2918eef91d0cdb9e900463c1feaf8bcf'),
    value: 'start with'
  },
  {
    label: i18n.global.t('libs.flow.const.c14a99f7d70ec35b9c189e4b3efee14b'),
    value: 'end with'
  }
];
export const fileTypeConfigOptions = [
  {
    label: i18n.global.t('libs.flow.const.2e22a75b5572dedf234c137985213069'),
    value: 'in'
  },
  {
    label: i18n.global.t('libs.flow.const.93ecc77504a036c0f6adc88f293b39e4'),
    value: 'not in'
  }
];

// File search - tree select options for file_name operator
export const fileNameOperatorTreeOptions = [
  {
    title: i18n.global.t('libs.flow.const.d84b20051d94243503f2f4df5df2a86e'),
    value: 'smart_match_group',
    key: 'smart_match_group',
    selectable: false,
    children: [
      {
        title: i18n.global.t(
          'libs.flow.const.bb56697fb10dc45b751446597c40dbc2'
        ),
        value: 'smart search',
        key: 'smart search'
      }
    ]
  },
  {
    title: i18n.global.t('libs.flow.const.2f9b339863d4813eaea7f0ba1a203281'),
    value: 'exact_match_group',
    key: 'exact_match_group',
    selectable: false,
    children: [
      {
        title: i18n.global.t(
          'libs.flow.const.57a4d26f94764fbdf55f6977b5920ce0'
        ),
        value: 'contains',
        key: 'contains'
      },
      {
        title: i18n.global.t(
          'libs.flow.const.4449c13d7633323a81ebeb8ffa6cba36'
        ),
        value: 'not contains',
        key: 'not contains'
      },
      {
        title: i18n.global.t(
          'libs.flow.const.d86c28c50f44bb30dd2028e3b3cf772a'
        ),
        value: '=',
        key: '='
      },
      {
        title: i18n.global.t(
          'libs.flow.const.2918eef91d0cdb9e900463c1feaf8bcf'
        ),
        value: 'start with',
        key: 'start with'
      },
      {
        title: i18n.global.t(
          'libs.flow.const.c14a99f7d70ec35b9c189e4b3efee14b'
        ),
        value: 'end with',
        key: 'end with'
      }
    ]
  }
];
export const conditionTypes = [
  {
    label: i18n.global.t('libs.flow.const.fb3749f97163cad97a977c26874129b3'),
    value: 'file_name'
  },
  {
    label: i18n.global.t('libs.flow.const.c282537eb4a14bfe6c54ec13b0c899f8'),
    value: 'file_type'
  }
];
export const fileTypeOptions = [
  {
    label: 'docx',
    value: 'docx'
  },
  {
    label: 'doc',
    value: 'doc'
  },
  {
    label: 'pdf',
    value: 'pdf'
  },
  {
    label: 'ppt',
    value: 'ppt'
  },
  {
    label: 'pptx',
    value: 'pptx'
  },
  {
    label: 'txt',
    value: 'txt'
  },
  {
    label: 'md',
    value: 'md'
  },
  {
    label: 'html',
    value: 'html'
  },
  {
    label: 'png',
    value: 'png'
  },
  {
    label: 'jpeg',
    value: 'jpeg'
  },
  {
    label: 'jpg',
    value: 'jpg'
  },
  {
    label: 'heic',
    value: 'heic'
  },
  {
    label: 'xls',
    value: 'xls'
  },
  {
    label: 'xlsx',
    value: 'xlsx'
  },
  {
    label: 'csv',
    value: 'csv'
  }
];
